import { getImageUrl } from '@/utils/assets'
import {
  CUSTOM_WALLPAPER_CONFIGS,
  DEFAULT_THEME_WALLPAPER,
  WALLPAPER_STORAGE_KEY,
  Wallpaper,
} from '@/stores/wallpaper'

type PersistedWallpaper = {
  currentWallpaper?: string
  currentCategory?: 'preset' | 'systemCustom' | 'userCustom'
}

type PersistedTheme = {
  mode?: 'light' | 'dark' | 'system'
}

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

function isPresetId(id: string): boolean {
  return (Object.values(Wallpaper) as string[]).includes(id)
}

function resolveIsDark(): boolean {
  const theme = readJson<PersistedTheme>('rapoo-theme')
  const mode = theme?.mode ?? 'system'
  if (mode === 'dark') return true
  if (mode === 'light') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

/**
 * 首屏预设壁纸 URL（不依赖 views/index / DeviceStore）。
 * 组件/用户自定义壁纸无法在此阶段出图，返回 null。
 */
export function resolveEarlyPresetWallpaperUrl(isDark = resolveIsDark()): string | null {
  const persisted = readJson<PersistedWallpaper>(WALLPAPER_STORAGE_KEY)
  const category = persisted?.currentCategory ?? 'preset'
  const id = persisted?.currentWallpaper

  if (category === 'systemCustom' || category === 'userCustom') {
    if (category === 'systemCustom' && id && CUSTOM_WALLPAPER_CONFIGS[id]?.type === 'component') {
      return null
    }
    return null
  }

  const wallpaperId =
    id && isPresetId(id)
      ? id
      : isDark
        ? DEFAULT_THEME_WALLPAPER.dark
        : DEFAULT_THEME_WALLPAPER.light

  return getImageUrl(`home/homeBg/${wallpaperId}.webp`)
}

/** 立刻开始拉当前壁纸，并注入 preload */
export function preloadWallpaperImage(url: string): Promise<void> {
  if (typeof document !== 'undefined') {
    const existing = document.querySelector('link[data-rapoo-wallpaper-preload="1"]')
    if (!existing) {
      const link = document.createElement('link')
      link.rel = 'preload'
      link.as = 'image'
      link.href = url
      link.dataset.rapooWallpaperPreload = '1'
      document.head.appendChild(link)
    }
  }

  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = () => resolve()
    img.src = url
  })
}

/**
 * 在加载 App（进而拉起 SDK / DeviceStore）之前调用，
 * 让壁纸图与模块图并行下载。
 */
export function kickoffWallpaperPreload(): Promise<void> {
  const url = resolveEarlyPresetWallpaperUrl()
  if (!url) return Promise.resolve()
  return preloadWallpaperImage(url)
}
